/**
 * Vehicle type mappings for GTFS data
 * Generated from public/routes.txt (GTFS route types specification)
 * 
 * See: https://developers.google.com/transit/gtfs/reference#routestxt
 */

export interface VehicleTypeConfig {
  code: number;
  description: string;
  icon: string;
  color: string;
  displayName: string;
}

/**
 * Maps GTFS route type codes to vehicle type configurations
 * Used during GTFS preprocessing and at runtime for UI rendering
 */
export const VEHICLE_TYPE_MAP: Record<number, VehicleTypeConfig> = {
  // Railway Services
  100: { code: 100, description: 'Railway Service', icon: 'train', color: '#F68B1F', displayName: 'Train' },
  101: { code: 101, description: 'High Speed Rail Service', icon: 'train-fast', color: '#F68B1F', displayName: 'High Speed Rail' },
  102: { code: 102, description: 'Long Distance Trains', icon: 'train', color: '#F68B1F', displayName: 'Long Distance Train' },
  103: { code: 103, description: 'Inter Regional Rail Service', icon: 'train', color: '#F68B1F', displayName: 'Regional Train' },
  105: { code: 105, description: 'Sleeper Rail Service', icon: 'train', color: '#F68B1F', displayName: 'Sleeper Train' },
  106: { code: 106, description: 'Regional Rail Service', icon: 'train', color: '#F68B1F', displayName: 'Regional Train' },
  107: { code: 107, description: 'Tourist Railway Service', icon: 'train', color: '#F68B1F', displayName: 'Tourist Train' },
  108: { code: 108, description: 'Rail Shuttle (Within Complex)', icon: 'train', color: '#F68B1F', displayName: 'Shuttle Train' },
  109: { code: 109, description: 'Suburban Railway', icon: 'train', color: '#F68B1F', displayName: 'Suburban Train' },
  116: { code: 116, description: 'Rack and Pinion Railway', icon: 'train', color: '#F68B1F', displayName: 'Rack Railway' },

  // Coach Services
  200: { code: 200, description: 'Coach Service', icon: 'bus', color: '#EC0000', displayName: 'Coach' },
  201: { code: 201, description: 'International Coach Service', icon: 'bus', color: '#EC0000', displayName: 'International Coach' },
  202: { code: 202, description: 'National Coach Service', icon: 'bus', color: '#EC0000', displayName: 'National Coach' },
  204: { code: 204, description: 'Regional Coach Service', icon: 'bus', color: '#EC0000', displayName: 'Regional Coach' },

  // Urban Railway (Metro/Underground)
  400: { code: 400, description: 'Urban Railway Service', icon: 'metro', color: '#0089CA', displayName: 'Urban Railway' },
  401: { code: 401, description: 'Metro Service', icon: 'metro', color: '#0089CA', displayName: 'Metro' },
  402: { code: 402, description: 'Underground Service', icon: 'metro', color: '#0089CA', displayName: 'Underground' },
  403: { code: 403, description: 'Urban Railway Service', icon: 'metro', color: '#0089CA', displayName: 'Urban Railway' },
  405: { code: 405, description: 'Monorail', icon: 'train', color: '#0089CA', displayName: 'Monorail' },

  // Bus Services
  700: { code: 700, description: 'Bus Service', icon: 'bus', color: '#EC0000', displayName: 'Bus' },
  701: { code: 701, description: 'Regional Bus Service', icon: 'bus', color: '#EC0000', displayName: 'Regional Bus' },
  702: { code: 702, description: 'Express Bus Service', icon: 'bus', color: '#EC0000', displayName: 'Express Bus' },
  704: { code: 704, description: 'Local Bus Service', icon: 'bus', color: '#EC0000', displayName: 'Local Bus' },
  715: { code: 715, description: 'Demand and Response Bus Service', icon: 'bus', color: '#EC0000', displayName: 'On-Demand Bus' },

  // Trolleybus
  800: { code: 800, description: 'Trolleybus Service', icon: 'bus', color: '#EC0000', displayName: 'Trolleybus' },

  // Tram
  900: { code: 900, description: 'Tram Service', icon: 'tram', color: '#885A9A', displayName: 'Tram' },

  // Water Transport
  1000: { code: 1000, description: 'Water Transport Service', icon: 'ferry', color: '#00A0D7', displayName: 'Water Transport' },

  // Ferry
  1200: { code: 1200, description: 'Ferry Service', icon: 'ferry', color: '#00A0D7', displayName: 'Ferry' },

  // Aerial Lift
  1300: { code: 1300, description: 'Aerial Lift Service', icon: 'cable-car', color: '#8C8C8C', displayName: 'Aerial Lift' },
  1301: { code: 1301, description: 'Telecabin Service', icon: 'cable-car', color: '#8C8C8C', displayName: 'Telecabin' },

  // Funicular
  1400: { code: 1400, description: 'Funicular Service', icon: 'train', color: '#F68B1F', displayName: 'Funicular' },

  // Taxi Services
  1501: { code: 1501, description: 'Communal Taxi Service', icon: 'taxi', color: '#FFD700', displayName: 'Communal Taxi' },
};

/**
 * Get vehicle type configuration by route type code
 * Falls back to a generic 'bus' type if code not found
 */
export function getVehicleType(routeTypeCode: number | string): VehicleTypeConfig {
  const code = typeof routeTypeCode === 'string' ? parseInt(routeTypeCode, 10) : routeTypeCode;
  
  return (
    VEHICLE_TYPE_MAP[code] || {
      code,
      description: 'Unknown Route Type',
      icon: 'bus',
      color: '#8C8C8C',
      displayName: 'Transit'
    }
  );
}

/**
 * Get all supported vehicle type codes
 */
export function getSupportedVehicleTypes(): number[] {
  return Object.keys(VEHICLE_TYPE_MAP).map(k => parseInt(k, 10));
}
